<script setup lang="ts">
import { ref, watch } from 'vue';
import { shiftDate, today } from '@/utils/date';
import { useUpsertRecord } from '@/composables/useSectorData';
import { DailyRecord } from '@/types/sector';

const props = defineProps<{
  dailyData: DailyRecord[];
}>();

const emit = defineEmits<{
  saved: [msg: string];
  error: [msg: string];
  import: [];
  clear: [];
}>();

const date = ref(today());
const first = ref('');
const second = ref('');
const third = ref('');
const submitting = ref(false);
const upsert = useUpsertRecord();

function fillFromDate() {
  const found = props.dailyData.find((d) => d.date === date.value);
  if (found) {
    first.value = found.first;
    second.value = found.second;
    third.value = found.third;
  } else {
    first.value = '';
    second.value = '';
    third.value = '';
  }
}

watch(date, fillFromDate, { immediate: true });

function shiftDay(days: number) {
  date.value = shiftDate(date.value, days);
}

async function submit() {
  if (!date.value || !first.value.trim() || !second.value.trim() || !third.value.trim()) {
    emit('error', '请填写所有字段');
    return;
  }
  submitting.value = true;
  try {
    await upsert.mutateAsync({
      date: date.value,
      first: first.value.trim(),
      second: second.value.trim(),
      third: third.value.trim(),
    });
    const existing = props.dailyData.find((d) => d.date === date.value);
    emit('saved', existing ? '数据已更新' : '数据已保存');
    first.value = '';
    second.value = '';
    third.value = '';
  } catch (e) {
    emit('error', (e as Error).message ?? '保存失败');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <section class="card">
    <h2 class="mb-4 flex items-center gap-2 text-base font-semibold">
      <span class="h-1.5 w-1.5 rounded-full" style="background: rgb(var(--color-primary));"></span>
      今日板块涨幅输入
    </h2>

    <form class="space-y-4" @submit.prevent="submit">
      <div>
        <label class="label" for="date">日期</label>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button type="button" class="btn btn-ghost text-xs" @click="shiftDay(-1)">◀ 前一日</button>
          <input id="date" v-model="date" type="date" class="input flex-1" required />
          <button type="button" class="btn btn-ghost text-xs" @click="shiftDay(1)">后一日 ▶</button>
        </div>
        <p v-if="dailyData.find(d => d.date === date)" class="mt-1 text-xs text-warning">
          ⚠ 该日期已存在数据，保存将覆盖
        </p>
      </div>

      <div>
        <label class="label" for="first">第一名 <span class="badge ml-1">权重 50%</span></label>
        <input id="first" v-model="first" type="text" class="input" placeholder="请输入板块名称" maxlength="20" required />
      </div>

      <div>
        <label class="label" for="second">第二名 <span class="badge ml-1">权重 30%</span></label>
        <input id="second" v-model="second" type="text" class="input" placeholder="请输入板块名称" maxlength="20" required />
      </div>

      <div>
        <label class="label" for="third">第三名 <span class="badge ml-1">权重 20%</span></label>
        <input id="third" v-model="third" type="text" class="input" placeholder="请输入板块名称" maxlength="20" required />
      </div>

      <div class="grid grid-cols-2 gap-2 pt-2">
        <button type="submit" class="btn btn-primary col-span-2" :disabled="submitting">
          <span v-if="submitting" class="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
          {{ submitting ? '保存中...' : '保存数据' }}
        </button>
        <button type="button" class="btn btn-ghost" @click="emit('import')">📥 导入 CSV</button>
        <a class="btn btn-ghost" :href="`/api/export`" target="_blank" rel="noopener">📤 导出 CSV</a>
        <button type="button" class="btn btn-danger col-span-2" @click="emit('clear')">🗑 清空所有数据</button>
      </div>
    </form>
  </section>
</template>
